import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Truck, Search, MapPin, Calendar, Package } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'

export default function ShipmentTrackingPage() {
  const navigate = useNavigate()
  const shipments = useInventoryStore((state) => state.shipments)

  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  const filteredShipments = useMemo(() => {
    return shipments.filter(shipment => {
      const matchesSearch = shipment.trackingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           shipment.supplier.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesFilter = filterStatus === 'all' || shipment.status === filterStatus
      return matchesSearch && matchesFilter
    })
  }, [shipments, searchTerm, filterStatus])

  const getStatusColor = (status) => {
    switch(status) {
      case 'in_transit': return 'bg-blue-500/20 text-blue-300'
      case 'delivered': return 'bg-green-500/20 text-green-300'
      case 'pending': return 'bg-yellow-500/20 text-yellow-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getStatusIcon = (status) => {
    switch(status) {
      case 'in_transit': return '📦 In Transit'
      case 'delivered': return '✅ Delivered'
      case 'pending': return '⏳ Pending'
      default: return '❓ Unknown'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
          <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
            <Truck className="w-8 h-8 text-pink-400" />
            <span>Shipment Tracking</span>
          </h1>
        </div>

        {/* Search & Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
            <input
              type="text"
              placeholder="Search by tracking number or supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-800 border border-blue-500/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-400/50"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
          </select>
        </div>

        {/* Shipments List */}
        <div className="space-y-4">
          {filteredShipments.map((shipment) => (
            <div key={shipment.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-white">{shipment.trackingNumber}</h3>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${getStatusColor(shipment.status)}`}>
                      {getStatusIcon(shipment.status)}
                    </span>
                  </div>
                  <p className="text-blue-300">{shipment.supplier}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                    <p className="text-blue-300 text-sm font-semibold">Origin</p>
                  </div>
                  <p className="text-white font-semibold text-sm">{shipment.origin}</p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                    <p className="text-blue-300 text-sm font-semibold">Destination</p>
                  </div>
                  <p className="text-white font-semibold text-sm">{shipment.destination}</p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    <p className="text-blue-300 text-sm font-semibold">Departure</p>
                  </div>
                  <p className="text-white font-semibold text-sm">{shipment.departureDate}</p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 text-cyan-400" />
                    <p className="text-blue-300 text-sm font-semibold">Expected Arrival</p>
                  </div>
                  <p className="text-white font-semibold text-sm">{shipment.expectedArrival}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="w-4 h-4 text-blue-400" />
                    <p className="text-blue-300 text-sm font-semibold">Items</p>
                  </div>
                  <p className="text-white text-lg font-bold">{shipment.items}</p>
                </div>

                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                  <p className="text-blue-300 text-sm font-semibold mb-2">Weight</p>
                  <p className="text-white text-lg font-bold">{shipment.weight}</p>
                </div>

                <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                  <p className="text-blue-300 text-sm font-semibold mb-2">Days in Transit</p>
                  <p className="text-white text-lg font-bold">
                    {Math.ceil((new Date(shipment.expectedArrival) - new Date(shipment.departureDate)) / (1000 * 60 * 60 * 24))}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredShipments.length === 0 && (
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-12 text-center">
            <Truck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-400">No shipments found</p>
          </div>
        )}
      </div>
    </div>
  )
}
